'use client';

import type { ReactElement, ReactNode } from 'react';
import React, { createContext, useMemo } from 'react';

import type { TreatsAction, TreatsStateProps } from './useTreatsReducer';
import { initialTreatsState, useTreatsReducer } from './useTreatsReducer';

/** ------------------------------------
 * context
 * ---------------------------------- */

export type TreatsDispatch = React.Dispatch<TreatsAction>;

export interface TreatsContextProps {
    dispatch: TreatsDispatch;
    treatsAppState: TreatsStateProps;
}

export const TreatsContext = createContext<TreatsContextProps>({
    dispatch: () => undefined,
    treatsAppState: initialTreatsState,
});

/** ------------------------------------
 * provider
 * ---------------------------------- */

export interface TreatsProviderProps {
    children: ReactNode;
    initialData: Partial<TreatsStateProps>;
}

export function TreatsProvider({ children, initialData }: TreatsProviderProps): ReactElement {
    const [treatsAppState, dispatch] = useTreatsReducer(initialData);

    const value = useMemo(
        () => ({
            dispatch,
            treatsAppState,
        }),
        [treatsAppState, dispatch]
    );

    return <TreatsContext.Provider value={value}>{children}</TreatsContext.Provider>;
}
